import type { ColumnPosition } from "building-types/columns/porchColumnPattern";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { ColonnadePoint } from "building-types/porches/porchColonnadePoint";
import { ColonnadePointType } from "building-types/porches/porchColonnadePoint";
import type { PorchKeyMeasures } from "building-types/porches/porchKeyMeasuresTypes";
import type { ExteriorMaterialOverrideWithKeyMeasures } from "building-types/porches/porchKeyMeasuresTypes";
import type { Prop, PropAttributeTable } from "building-types/props/propTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import type { ColumnCatalogSettings } from "building-types/settings/columnCatalogSettings";
import type { PlanSettings } from "building-types/settings/planSettings";
import type { PorchSettingsBase } from "building-types/settings/porchSettingsTypes";
import {
  ColonnadeTerminationType,
  ColumnDimensionType,
  DoorPathColumnSpreadMode,
} from "building-types/settings/settingsTypesEnum";
import type { StructureSettings } from "building-types/settings/structureSettings";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import type { MergedPropLibrary } from "doc/propTools";
import { ANGLE_EPSILON, DIST_EPSILON } from "geom/core";
import type GeomTagGroup from "geom/geomTagGroup";
import { polyLine2MiterPt } from "geom/offset";
import { polyLine2EdgeTangent, polyLine2IsClosed } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { rangeNonInclusiveByMinStep } from "geom/range";
import { ascendingSortCompare } from "geom/utils";
import type { Vec2 } from "geom/vec2";
import { vec2AngleBetween, vec2Dist, vec2Lerp, vec2NormsCollinear } from "geom/vec2";
import { cutGapsIntoInterval } from "../../utils/intervals";
import { columnPositionsToNormalizedParams } from "../../utils/porchColumnPatternTools";
import { ColumnBuilder } from "./porchColumn";
import type { EdgeIntersectionParams } from "./porchEdgeLayout";
import { placeColumnIfUnsupportedVertexAbove } from "./porchEdgeLayout";
import {
  getColumnFromSettings,
  resolvePorchPlanSettings,
  splitIntervalAtParams,
  type PorchSettingsColumnPlacement,
  type ResolvedPorchSettingsAndDefaults,
} from "./porchUtils";

enum ColonnadeEdgeType {
  FIRST = "FIRST",
  MIDDLE = "MIDDLE",
  LAST = "LAST",
  ONLY = "ONLY",
}

interface MakeColonnadeOptions {
  readonly resolvedPorchSettings: ResolvedPorchSettingsAndDefaults;
  readonly columnSettings: ColumnCatalogSettings;
  readonly structureSettings: StructureSettings;
  readonly propCatalog: PropAttributeTable;
  readonly mergedPropLibrary: MergedPropLibrary;
  readonly porchSettings: PorchSettingsBase;
  readonly planSettings: PlanSettings;
  readonly doorPathIntersections: EdgeIntersectionParams;
  readonly segment: PolyLine2;
  readonly firstColumnSectionWidth: number;
  readonly unsupportedCornerAbovePts: Vec2[];
  readonly startTermType: ColonnadeTerminationType;
  readonly endTermType: ColonnadeTerminationType;
  readonly mirror: boolean;
  readonly edgeIndexToColumnPositionsTable: { [edgeIndex: number]: ColumnPosition[] | null } | null;
  readonly topDatum: number;
  readonly bottomDatum: number;
  readonly room: Room;
  readonly props: Prop[];
  readonly group: GeomTagGroup<ExteriorMaterialOverrideWithKeyMeasures>;
  readonly columnProfiles: PolyLine2[];
  readonly colonnadePoints: ColonnadePoint[];
  readonly keyMeasures: PorchKeyMeasures;
  readonly halfEdgeArr: (number | undefined)[][];
}

export async function makePorchColonnade({
  segment,
  structureSettings,
  columnSettings,
  propCatalog,
  mergedPropLibrary,
  porchSettings,
  planSettings,
  unsupportedCornerAbovePts,
  doorPathIntersections,
  resolvedPorchSettings,
  firstColumnSectionWidth,
  startTermType,
  endTermType,
  mirror,
  edgeIndexToColumnPositionsTable,
  topDatum,
  bottomDatum,
  room,
  props,
  group,
  columnProfiles,
  colonnadePoints,
  keyMeasures,
  halfEdgeArr,
}: MakeColonnadeOptions) {
  const { multiColumn, columnCasingOffset, internalColumnStructureSize } = resolvedPorchSettings;
  const internalColumnStructureHalfSize = internalColumnStructureSize / 2;
  const column = getColumnFromSettings(resolvedPorchSettings, columnSettings);
  keyMeasures.name = column?.name || "";
  const columnBuilder = new ColumnBuilder(
    bottomDatum,
    topDatum,
    resolvedPorchSettings,
    colonnadePoints,
    columnProfiles,
    group,
    propCatalog,
    mergedPropLibrary,
    props,
    room,
    keyMeasures,
    columnCasingOffset,
    porchSettings,
    planSettings,
  );

  const resolvedPorchPlanSettings = resolvePorchPlanSettings(planSettings, porchSettings);

  // these (sections and view notes) could occur inside of the column builder class rather than being passed outside
  const sections = column?.sections || [];
  const columnViewNotes = column?.viewNotes;

  const closed = polyLine2IsClosed(segment);
  const isFrontPorch = room.type === RoomType.FRONT_PORCH;
  const [frontEdgeX, frontEdgeY] = isFrontPorch ? [1, 0] : [-1, 0];

  // When dimensioning using CENTERLINE mode, the corner points must be the center of column structure,
  // It's necessary to avoid offsetting the terminus point in that case - otherwise we end up with
  // non-colinear dimensions on that edge.
  const offsetTerminus =
    planSettings.columnDimensionType !== ColumnDimensionType.CENTERLINE &&
    resolvedPorchPlanSettings.includePorchColumnStructure;

  const colonnadeEdgeTable = makeColonnadeEdgeTable(
    segment,
    structureSettings,
    unsupportedCornerAbovePts,
    edgeIndexToColumnPositionsTable,
    doorPathIntersections,
    resolvedPorchSettings,
    mirror,
    frontEdgeX,
    frontEdgeY,
  );

  remapCornerColumnParamsFromEndOfEdgeToStartOfNextEdge(colonnadeEdgeTable, closed);

  const {
    reverseTangentX,
    reverseTangentY,
    isMultiColumn: lastIsMultiColumn,
  } = colonnadeEdgeTable[Object.keys(colonnadeEdgeTable).length - 1];

  //set previous edge values for closed colonnade segments
  let previousReverseTangentX = reverseTangentX;
  let previousReverseTangentY = reverseTangentY;
  let previousIsMultiColumnSegment = lastIsMultiColumn;
  const colonnadeEdgeTableKeys = Object.keys(colonnadeEdgeTable);

  for (let i = 0; i < colonnadeEdgeTableKeys.length; i++) {
    const key = colonnadeEdgeTableKeys[i];
    const halfEdges = halfEdgeArr[i];
    const {
      ax,
      ay,
      bx,
      by,
      tangentX,
      tangentY,
      reverseTangentX,
      reverseTangentY,
      colonnadeEdgeType,
      columnParams,
      isMultiColumn,
      columnRotations,
    } = colonnadeEdgeTable[key];

    if (colonnadeEdgeType === ColonnadeEdgeType.FIRST || colonnadeEdgeType === ColonnadeEdgeType.ONLY) {
      if (columnParams.length === 0 && !closed) {
        colonnadePoints.push({
          type: ColonnadePointType.TERMINUS,
          x: ax + (offsetTerminus ? tangentY * internalColumnStructureHalfSize : 0),
          y: ay - (offsetTerminus ? tangentX * internalColumnStructureHalfSize : 0),
          halfEdges,
        });
      }
    } else if (
      (columnParams.length === 0 || columnParams[0] > DIST_EPSILON) &&
      !vec2NormsCollinear(reverseTangentX, reverseTangentY, previousReverseTangentX, previousReverseTangentY)
    ) {
      let x = ax;
      let y = ay;

      if (
        planSettings.columnDimensionType !== ColumnDimensionType.CENTERLINE &&
        resolvedPorchPlanSettings.includePorchColumnStructure
      ) {
        [x, y] = polyLine2MiterPt(
          ax + previousReverseTangentX,
          ay + previousReverseTangentY,
          ax,
          ay,
          ax + tangentX,
          ay + tangentY,
          internalColumnStructureHalfSize,
        );
      }

      if (planSettings.columnDimensionType !== ColumnDimensionType.NONE) {
        columnBuilder.colonnadePoints.push({
          type: ColonnadePointType.COLUMN_DIM,
          x,
          y,
          halfEdges,
        });
      }
    }

    for (let j = 0, lj = columnParams.length; j < lj; j++) {
      const t = columnParams[j];
      const rotated = columnRotations ? columnRotations[j] : false;
      const [x, y] = vec2Lerp(ax, ay, bx, by, t);
      const adjDist = firstColumnSectionWidth;
      const adjustX = tangentX * adjDist;
      const adjustY = tangentY * adjDist;
      const isStartOfColonnadeEdge = t < DIST_EPSILON;
      const isEndOfColonnadeEdge = Math.abs(1 - t) < DIST_EPSILON;
      const isStartOrEndOfColonnadeEdge = isStartOfColonnadeEdge || isEndOfColonnadeEdge;

      if (
        (colonnadeEdgeType === ColonnadeEdgeType.FIRST || colonnadeEdgeType === ColonnadeEdgeType.ONLY) &&
        isStartOfColonnadeEdge
      ) {
        if (!closed) {
          colonnadePoints.push({
            type: ColonnadePointType.TERMINUS,
            x: ax + (offsetTerminus ? tangentY * internalColumnStructureHalfSize : 0),
            y: ay - (offsetTerminus ? tangentX * internalColumnStructureHalfSize : 0),
            halfEdges,
          });
        }

        if (startTermType !== ColonnadeTerminationType.NO_COLUMN) {
          await columnBuilder.makeSingleOrMultiColumn(
            false,
            sections,
            x + adjustX,
            y + adjustY,
            isStartOrEndOfColonnadeEdge,
            [tangentX, tangentY],
            [0, 0],
            [[tangentX, tangentY]],
            isMultiColumn,
            columnViewNotes,
            rotated,
            undefined,
            halfEdges,
          );
        }

        if (closed) {
          const { braceVecs, addPreviousMultiColumn } = getBraceVecsAndDetermineWhetherToAddMultiColumnFromPreviousEdge(
            multiColumn,
            previousReverseTangentX,
            previousReverseTangentY,
            tangentX,
            tangentY,
            isMultiColumn,
            previousIsMultiColumnSegment,
          );

          await columnBuilder.makeSingleOrMultiColumn(
            true,
            sections,
            x,
            y,
            isStartOrEndOfColonnadeEdge,
            [tangentX, tangentY],
            [previousReverseTangentX, previousReverseTangentY],
            braceVecs,
            isMultiColumn,
            columnViewNotes,
            rotated,
            addPreviousMultiColumn,
            halfEdges,
          );
        }
      } else if (
        (colonnadeEdgeType === ColonnadeEdgeType.LAST || colonnadeEdgeType === ColonnadeEdgeType.ONLY) &&
        isEndOfColonnadeEdge
      ) {
        if (endTermType !== ColonnadeTerminationType.NO_COLUMN) {
          await columnBuilder.makeSingleOrMultiColumn(
            false,
            sections,
            x - adjustX,
            y - adjustY,
            isStartOrEndOfColonnadeEdge,
            [tangentX, tangentY],
            [previousReverseTangentX, previousReverseTangentY],
            [[reverseTangentX, reverseTangentY]],
            isMultiColumn,
            columnViewNotes,
            rotated,
            undefined,
            halfEdges,
          );
        }
      } else if (isStartOfColonnadeEdge) {
        const { braceVecs, addPreviousMultiColumn } = getBraceVecsAndDetermineWhetherToAddMultiColumnFromPreviousEdge(
          multiColumn,
          previousReverseTangentX,
          previousReverseTangentY,
          tangentX,
          tangentY,
          isMultiColumn,
          previousIsMultiColumnSegment,
        );
        await columnBuilder.makeSingleOrMultiColumn(
          true,
          sections,
          x,
          y,
          isStartOrEndOfColonnadeEdge,
          [tangentX, tangentY],
          [previousReverseTangentX, previousReverseTangentY],
          braceVecs,
          isMultiColumn,
          columnViewNotes,
          rotated,
          addPreviousMultiColumn,
          halfEdges,
        );
      } else if (isEndOfColonnadeEdge) {
        //do nothing since ends are converted into starts of the next colonnade edge (except the last one, which is handled above)
      } else {
        // middle columns
        await columnBuilder.makeSingleOrMultiColumn(
          false,
          sections,
          x,
          y,
          isStartOrEndOfColonnadeEdge,
          [tangentX, tangentY],
          [previousReverseTangentX, previousReverseTangentY],
          [
            [tangentX, tangentY],
            [reverseTangentX, reverseTangentY],
          ],
          isMultiColumn,
          columnViewNotes,
          rotated,
          undefined,
          halfEdges,
        );
      }
    }

    if (colonnadeEdgeType === ColonnadeEdgeType.LAST || colonnadeEdgeType === ColonnadeEdgeType.ONLY) {
      if (!closed) {
        colonnadePoints.push({
          type: ColonnadePointType.TERMINUS,
          x: bx + (offsetTerminus ? tangentY * internalColumnStructureHalfSize : 0),
          y: by - (offsetTerminus ? tangentX * internalColumnStructureHalfSize : 0),
          halfEdges,
        });
      }
    }

    previousReverseTangentX = reverseTangentX;
    previousReverseTangentY = reverseTangentY;
    previousIsMultiColumnSegment = isMultiColumn;
  }
}

interface ColonnadeEdgeInfo {
  readonly tangentX: number;
  readonly tangentY: number;
  readonly reverseTangentX: number;
  readonly reverseTangentY: number;

  readonly ax: number;
  readonly ay: number;
  readonly bx: number;
  readonly by: number;

  readonly isFrontColonnadeEdge: boolean;
  readonly colonnadeEdgeType: ColonnadeEdgeType;
  readonly columnParams: number[];
  readonly columnRotations?: boolean[];
  readonly isMultiColumn: boolean;
}
type ColonnadeEdgeTable = { [edgeIndex: number]: ColonnadeEdgeInfo };

function makeColonnadeEdgeTable(
  segment: PolyLine2,
  structureSettings: StructureSettings,
  unsupportedCornerAbovePts: Vec2[],
  edgeIndexToColumnPositionsTable: { [edgeIndex: number]: ColumnPosition[] | null } | null,
  doorPathIntersections: EdgeIntersectionParams,
  resolvedPorchSettings: ResolvedPorchSettingsAndDefaults,
  mirror: boolean,
  frontEdgeX: number,
  frontEdgeY: number,
): ColonnadeEdgeTable {
  const colonnadeEdgeTable: ColonnadeEdgeTable = {};

  for (let i = 0, l = segment.length - 2; i < l; i += 2) {
    const ax = segment[i];
    const ay = segment[i + 1];
    const bx = segment[i + 2];
    const by = segment[i + 3];
    const dist = vec2Dist(ax, ay, bx, by);
    const edgeIndex = i / 2;
    const [fx, fy] = dist < DIST_EPSILON ? [1, 0] : polyLine2EdgeTangent(segment, edgeIndex);
    const [rx, ry] = dist < DIST_EPSILON ? [-1, 0] : polyLine2EdgeTangent(segment, edgeIndex, true);
    const colonnadeEdgeType = getColonnadeEdgeType(segment.length, i);
    const isFrontColonnadeEdge = Math.abs(vec2AngleBetween(fx, fy, frontEdgeX, frontEdgeY)) < ANGLE_EPSILON;
    const { multiColumn, multiColumnFrontOnly } = resolvedPorchSettings;
    const isMultiColumn = multiColumn && (multiColumnFrontOnly ? isFrontColonnadeEdge : true);
    const unsupportedCornerAboveParams = placeColumnIfUnsupportedVertexAbove(
      structureSettings,
      unsupportedCornerAbovePts,
      polyLine2New([ax, ay, bx, by]),
    );

    const colonnadeEdgeInfoBase = {
      colonnadeEdgeType,
      ax,
      ay,
      bx,
      by,
      tangentX: fx,
      tangentY: fy,
      reverseTangentX: rx,
      reverseTangentY: ry,
      isFrontColonnadeEdge,
      isMultiColumn,
    };

    const explicitColumnPositions = edgeIndexToColumnPositionsTable ? edgeIndexToColumnPositionsTable[i / 2] : null;

    if (explicitColumnPositions) {
      const [columnParams, columnRotations] = columnPositionsToNormalizedParams(explicitColumnPositions, dist, mirror);
      colonnadeEdgeTable[edgeIndex] = {
        columnParams,
        ...colonnadeEdgeInfoBase,
        columnRotations,
        isMultiColumn: !!colonnadeEdgeInfoBase.isMultiColumn,
      };
    } else {
      const columnParams = interiorColumnParamsIncludingBreaks(
        edgeIndex,
        doorPathIntersections,
        dist,
        resolvedPorchSettings,
        unsupportedCornerAboveParams,
        isFrontColonnadeEdge,
        mirror,
      );
      colonnadeEdgeTable[edgeIndex] = {
        columnParams: columnParams.sort(ascendingSortCompare) || [],
        ...colonnadeEdgeInfoBase,
        isMultiColumn: !!colonnadeEdgeInfoBase.isMultiColumn,
      };
    }
  }
  return colonnadeEdgeTable;
}

function remapCornerColumnParamsFromEndOfEdgeToStartOfNextEdge(colonnadeEdgeMap: ColonnadeEdgeTable, closed: boolean) {
  const keys = Object.keys(colonnadeEdgeMap);
  const colonnadeKeysLength = keys.length;

  if (!colonnadeEdgeMap || colonnadeKeysLength === 0) {
    return;
  }

  let addFirstColumn = false;
  let rotation: boolean | undefined = false;
  for (const key in colonnadeEdgeMap) {
    const { columnParams, columnRotations } = colonnadeEdgeMap[key];

    if (addFirstColumn) {
      addFirstColumn = false;

      if (columnParams.length === 0 || (columnParams.length > 0 && columnParams[0] > DIST_EPSILON)) {
        columnParams.unshift(0);
        columnRotations?.unshift(!!rotation);
      }
    }

    if (columnParams.length > 0 && Math.abs(columnParams[columnParams.length - 1] - 1) < DIST_EPSILON) {
      if (!closed && key === keys[colonnadeKeysLength - 1]) {
        continue;
      }

      columnParams.pop();
      rotation = columnRotations?.pop();
      addFirstColumn = true;

      if (closed && key === keys[colonnadeKeysLength - 1]) {
        const { columnParams: firstColumnParams, columnRotations: firstColumnRotations } = colonnadeEdgeMap[0];
        if (firstColumnParams.length === 0 || (firstColumnParams.length > 0 && firstColumnParams[0] > DIST_EPSILON)) {
          firstColumnParams.unshift(0);
          firstColumnRotations?.unshift(!!rotation);
        }
      }
    }
  }
}

function getBraceVecsAndDetermineWhetherToAddMultiColumnFromPreviousEdge(
  multiColumn: boolean | undefined,
  previousReverseTangentX: number,
  previousReverseTangentY: number,
  tangentX: number,
  tangentY: number,
  isMultiColumnSegment: boolean,
  previousIsMultiColumnSegment: boolean,
) {
  let braceVecs: Vec2[] | undefined = undefined;
  let addPreviousMultiColumn = false;

  if (!multiColumn) {
    braceVecs = [
      [previousReverseTangentX, previousReverseTangentY],
      [tangentX, tangentY],
    ];
  } else if (isMultiColumnSegment && previousIsMultiColumnSegment) {
    braceVecs = [
      [previousReverseTangentX, previousReverseTangentY],
      [tangentX, tangentY],
    ];
  } else if (isMultiColumnSegment) {
    braceVecs = [[tangentX, tangentY]];
  } else if (previousIsMultiColumnSegment) {
    addPreviousMultiColumn = true;
    braceVecs = [[previousReverseTangentX, previousReverseTangentY]];
  }
  return { braceVecs, addPreviousMultiColumn };
}

export function interiorColumnParamsIncludingBreaks(
  i: number,
  gapsEdgeRefTable: EdgeIntersectionParams,
  dist: number,
  porchSettings: PorchSettingsColumnPlacement,
  additionalColumnParams: number[],
  front: boolean,
  mirror: boolean,
): number[] {
  const gapTs = gapsEdgeRefTable[i];
  let interiorColumnParams: number[] = [];

  const gaps: number[] = [];

  const requiredColumnSpacingForMaxSpan: number[] = rangeNonInclusiveByMinStep(
    0,
    1.0,
    porchSettings.maxColumnSpacing / dist,
  );

  let anyGapsAdded = false;
  if (requiredColumnSpacingForMaxSpan.length > 0 && porchSettings.doorPathColumnSpread && gapTs?.length > 0) {
    anyGapsAdded = determineIfGapsAddedToOriginalColumnParams(gapTs, porchSettings, dist, gaps, anyGapsAdded);
  }

  if (anyGapsAdded) {
    const newParams = cutGapsIntoInterval(0, 1, gaps);
    for (let i = 0, l = newParams.length; i < l; i += 2) {
      const paramExtrema = [newParams[i], ...additionalColumnParams, newParams[i + 1]];
      interiorColumnParams.push(...paramExtrema);
      const newSubParams = splitIntervalAtParams(newParams[i], newParams[i + 1], additionalColumnParams);
      for (let i = 0, l = newSubParams.length; i < l; i += 2) {
        const start = newSubParams[i];
        const end = newSubParams[i + 1];
        const subParamInterior = rangeNonInclusiveByMinStep(start, end, porchSettings.maxColumnSpacing / dist);
        interiorColumnParams.push(...subParamInterior);
      }
    }
  } else {
    const paramExtrema = [0, ...additionalColumnParams, 1];
    interiorColumnParams.push(...paramExtrema);
    const newSubParams = splitIntervalAtParams(0, 1, additionalColumnParams);
    for (let i = 0, l = newSubParams.length; i < l; i += 2) {
      const start = newSubParams[i];
      const end = newSubParams[i + 1];
      const subParamInterior = rangeNonInclusiveByMinStep(start, end, porchSettings.maxColumnSpacing / dist);
      interiorColumnParams.push(...subParamInterior);
    }
  }

  interiorColumnParams.sort(ascendingSortCompare);

  if (porchSettings.minColumnSpacing !== undefined) {
    interiorColumnParams = addColumnParamsByMinColumnSpacing(dist, porchSettings, interiorColumnParams);
  }

  const colonnadeOverrideValues = porchSettings.colonnadeOverrideValues ?? [0, 1];
  const paramOverrides: number[] = [];
  const mirrorParams: number[] = [];

  if (front) {
    if (porchSettings.frontColonnadeColumnOverride) {
      for (const num of colonnadeOverrideValues) {
        if (num > 1 || num < -1) {
          num < 0 ? paramOverrides.push(num / dist + 1) : paramOverrides.push(num / dist);
        } else {
          num < 0 ? paramOverrides.push(num + 1) : paramOverrides.push(num);
        }
      }

      interiorColumnParams = paramOverrides ?? [0, 0.5, 1];

      if (mirror) {
        for (const num of colonnadeOverrideValues) {
          if (num > 1 || num < -1) {
            num < 0 ? mirrorParams.push(Math.abs(num) / dist) : mirrorParams.push(1 - num / dist);
          } else {
            num < 0 ? mirrorParams.push(Math.abs(num)) : mirrorParams.push(1 - num);
          }
        }
        interiorColumnParams = mirrorParams.sort() ?? [0, 0.5, 1];
      }
    }
  }

  // get all of the edge lengths
  return interiorColumnParams;
}

function addColumnParamsByMinColumnSpacing(
  dist: number,
  porchSettings: PorchSettingsColumnPlacement,
  interiorColumnParams: number[],
) {
  const params: number[] = [];

  if (porchSettings.minColumnSpacing !== undefined && dist > porchSettings.minColumnSpacing) {
    for (let i = 0; i < interiorColumnParams.length - 1; i++) {
      const p0 = interiorColumnParams[i];
      const p1 = interiorColumnParams[i + 1];

      const len = dist * (p1 - p0);

      if (len < porchSettings.minColumnSpacing) {
        if (p0 === 0) {
          params.push(p0);
          i++;
          continue;
        }

        if (p1 === 1) {
          params.push(p1);
          break;
        }

        params.push((p0 + p1) / 2);
        i++;
      } else {
        params.push(p0);

        if (p1 === 1) {
          params.push(p1);
          break;
        }
      }
    }

    if (params[params.length - 1] !== 1 && dist * (1 - params[params.length - 1]) > porchSettings.minColumnSpacing) {
      params.length = params.length - 1;
    }
  }
  interiorColumnParams = params;
  return interiorColumnParams;
}

function determineIfGapsAddedToOriginalColumnParams(
  gapTs: { door: GeneratedDoorPlacement; param: number }[],
  porchSettings: PorchSettingsColumnPlacement,
  dist: number,
  gaps: number[],
  anyGapsAdded: boolean,
) {
  gapTs.sort((a, b) => a.param - b.param);

  for (const { param: t, door } of gapTs) {
    const doorWidth = door.descriptor.width;
    assertIsNotNullOrUndefined(porchSettings.doorPathColumnSpread);
    let spreadDistance: number = porchSettings.doorPathColumnSpread;
    const spreadMode = porchSettings.doorPathColumnSpreadMode ?? DoorPathColumnSpreadMode.FROM_CENTER_LINE;
    if (spreadMode === DoorPathColumnSpreadMode.FROM_DOOR_EDGES) {
      spreadDistance = doorWidth + porchSettings.doorPathColumnSpread;
    }
    const cancelDoorColumnSpreadWithinDist = 24 / dist;
    const removeColumnSpread = spreadDistance / dist;
    const removeColumnHalfSpread = removeColumnSpread / 2;

    const minT = t - removeColumnHalfSpread;
    const maxT = t + removeColumnHalfSpread;

    const minOutOfRange = minT <= cancelDoorColumnSpreadWithinDist;
    const maxOutOfRange = maxT >= 1 - cancelDoorColumnSpreadWithinDist;

    if (minOutOfRange && maxOutOfRange) {
      continue;
    }
    if (minOutOfRange) {
      if (spreadDistance > porchSettings.maxColumnSpacing) {
        gaps.push(0);
        gaps.push(maxT);
        anyGapsAdded = true;
      }
      continue;
    }

    if (maxOutOfRange) {
      if (spreadDistance > porchSettings.maxColumnSpacing) {
        gaps.push(minT);
        gaps.push(1);
        anyGapsAdded = true;
      }
      continue;
    }

    gaps.push(minT);
    gaps.push(maxT);

    anyGapsAdded = true;
    break;
  }
  return anyGapsAdded;
}

function getColonnadeEdgeType(segmentLength: number, segmentIndex: number): ColonnadeEdgeType {
  const vertexCount = segmentLength / 2;
  const edgeIndex = segmentIndex / 2;
  const edgeCount = vertexCount - 1;

  if (segmentIndex === 0 && edgeCount === 1) {
    return ColonnadeEdgeType.ONLY;
  }
  if (segmentIndex === 0) {
    return ColonnadeEdgeType.FIRST;
  }

  if (edgeIndex === edgeCount - 1) {
    return ColonnadeEdgeType.LAST;
  }

  return ColonnadeEdgeType.MIDDLE;
}
